import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { serviceAPI, getServerURL } from '../services/api';
import { Wrench, Calendar, DollarSign, User, Phone, Mail, Building, Package, Clock, CheckCircle, AlertCircle, Printer, ArrowLeft, Edit, FileUp } from 'lucide-react';
import DocumentViewer from '../components/DocumentViewer';

const ServiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchService();
  }, [id]);

  const fetchService = async () => {
    try {
      const response = await serviceAPI.getOne(id);
      setService(response.data);
      // Load documents from service data
      setDocuments(Array.isArray(response.data.documents) ? response.data.documents : []);
    } catch (error) {
      console.error('Error fetching service:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await serviceAPI.update(id, { status: newStatus });
      fetchService();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDocumentUpload = async (files, documentType = 'invoice') => {
    if (!files || files.length === 0) return;

    setUploadingDocuments(true);
    setError('');
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      formData.append('document_type', documentType);

      const token = localStorage.getItem('token');
      const response = await fetch(`${getServerURL()}/api/images/services/${id}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      setDocuments(data.documents);
      setSuccessMessage(`Successfully uploaded ${files.length} document(s)`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error uploading documents:', err);
      setError('Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
    }
  };

  const handleDeleteDocument = async (index) => {
    if (window.confirm('Delete this document?')) {
      try {
        setError('');
        const token = localStorage.getItem('token');
        const response = await fetch(`${getServerURL()}/api/images/services/${id}/documents/${index}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error('Delete failed');

        setDocuments(prev => prev.filter((_, i) => i !== index));
        setSuccessMessage('Document deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Error deleting document:', err);
        setError('Failed to delete document');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
      'In Progress': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Wrench },
      'Completed': { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      'Picked Up': { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: Package },
    };

    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border-2 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${(Number(amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Not Found</h2>
        <button
          onClick={() => navigate('/servicing')}
          className="mt-4 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800"
        >
          Back to Services
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 rounded-xl">
          <p className="font-medium">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border-2 border-green-200 text-green-800 px-4 py-3 rounded-xl">
          <p className="font-medium">{successMessage}</p>
        </div>
      )}

      {/* Action Buttons - Hidden when printing */}
      <div className="flex items-center justify-between print:hidden">
        <button
          onClick={() => navigate('/servicing')}
          className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Services
        </button>
        <div className="flex gap-2">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 font-semibold"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={() => navigate(`/servicing/edit/${id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-semibold"
          >
            <Edit className="w-4 h-4" />
            Edit Service
          </button>
        </div>
      </div>

      {/* Service Invoice */}
      <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-8 print:shadow-none print:border-0">
        {/* Header */}
        <div className="border-b-2 border-gray-200 pb-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Service Record</h1>
              <p className="text-gray-600">Service #{service.service_number}</p>
            </div>
            <div className="text-right">
              <div className="mb-2">{getStatusBadge(service.status)}</div>
              <p className="text-sm text-gray-600">Created: {formatDate(service.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Watch Information */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-slate-900" />
            Watch Information
          </h2>
          <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Brand</p>
              <p className="text-lg font-semibold text-gray-900">{service.watch_brand || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Model</p>
              <p className="text-lg font-semibold text-gray-900">{service.watch_model || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Reference Number</p>
              <p className="text-lg font-semibold text-gray-900">{service.watch_reference || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Serial Number</p>
              <p className="text-lg font-semibold text-gray-900">{service.watch_serial || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-slate-900" />
            Customer Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 rounded-xl p-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Name</p>
              <p className="text-lg font-semibold text-gray-900">{service.customer_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Email
              </p>
              <p className="text-lg font-semibold text-gray-900">{service.customer_email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                Phone
              </p>
              <p className="text-lg font-semibold text-gray-900">{service.customer_phone || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Service Provider */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Building className="w-5 h-5 text-slate-900" />
            Service Provider
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 rounded-xl p-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Provider Name</p>
              <p className="text-lg font-semibold text-gray-900">{service.service_provider}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Contact Person</p>
              <p className="text-lg font-semibold text-gray-900">{service.provider_contact || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                Phone
              </p>
              <p className="text-lg font-semibold text-gray-900">{service.provider_phone || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1 flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Email
              </p>
              <p className="text-lg font-semibold text-gray-900">{service.provider_email || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-900" />
            Service Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 rounded-xl p-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Service Type</p>
              <p className="text-lg font-semibold text-gray-900">{service.service_type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Current Status</p>
              <div className="mt-1">{getStatusBadge(service.status)}</div>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Received Date</p>
              <p className="text-lg font-semibold text-gray-900">{formatDate(service.received_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Estimated Completion</p>
              <p className="text-lg font-semibold text-gray-900">{formatDate(service.estimated_completion)}</p>
            </div>
            {service.actual_completion && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Actual Completion</p>
                <p className="text-lg font-semibold text-gray-900">{formatDate(service.actual_completion)}</p>
              </div>
            )}
            {service.picked_up_date && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Picked Up Date</p>
                <p className="text-lg font-semibold text-gray-900">{formatDate(service.picked_up_date)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Service Description */}
        {service.service_description && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Service Description</h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 whitespace-pre-wrap">{service.service_description}</p>
            </div>
          </div>
        )}

        {/* Work Performed */}
        {service.work_performed && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Work Performed</h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 whitespace-pre-wrap">{service.work_performed}</p>
            </div>
          </div>
        )}

        {/* Parts Replaced */}
        {service.parts_replaced && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Parts Replaced</h2>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 whitespace-pre-wrap">{service.parts_replaced}</p>
            </div>
          </div>
        )}

        {/* Costs */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-slate-900" />
            Financial Details
          </h2>
          <div className="bg-gradient-to-br from-slate-50 to-stone-50 rounded-xl p-6 border-2 border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Estimated Cost</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(service.estimated_cost)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Actual Cost</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(service.actual_cost)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Customer Charge</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(service.customer_charge)}</p>
              </div>
            </div>
            {service.actual_cost && service.customer_charge && (
              <div className="mt-6 pt-6 border-t-2 border-slate-200">
                <p className="text-sm text-gray-600 mb-1">Profit Margin</p>
                <p className="text-3xl font-bold text-amber-600">
                  {formatCurrency(parseFloat(service.customer_charge) - parseFloat(service.actual_cost))}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Internal Notes - Hidden when printing */}
        {service.internal_notes && (
          <div className="mb-8 print:hidden">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Internal Notes</h2>
            <div className="bg-yellow-50 rounded-xl p-6 border-2 border-yellow-200">
              <p className="text-gray-700 whitespace-pre-wrap">{service.internal_notes}</p>
            </div>
          </div>
        )}

        {/* Documents Section - Hidden when printing */}
        <div className="mb-8 print:hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Service Documents ({documents.length})</h2>
            <label className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl cursor-pointer hover:bg-amber-700 transition-all">
              <FileUp className="w-4 h-4" />
              Upload Documents
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx"
                className="hidden"
                onChange={(e) => handleDocumentUpload(e.target.files)}
                disabled={uploadingDocuments}
              />
            </label>
          </div>
          <DocumentViewer
            documents={documents}
            onDelete={handleDeleteDocument}
          />
          {uploadingDocuments && (
            <div className="text-center py-4 mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Uploading documents...</p>
            </div>
          )}
        </div>
      </div>

      {/* Status Update Buttons - Hidden when printing */}
      <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-6 print:hidden">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Update Service Status</h3>
        <div className="flex flex-wrap gap-3">
          {service.status !== 'Pending' && (
            <button
              onClick={() => handleUpdateStatus('Pending')}
              className="px-6 py-3 bg-yellow-500 text-white rounded-xl font-semibold hover:bg-yellow-600 transition-all"
            >
              Mark as Pending
            </button>
          )}
          {service.status !== 'In Progress' && (
            <button
              onClick={() => handleUpdateStatus('In Progress')}
              className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all"
            >
              Mark as In Progress
            </button>
          )}
          {service.status !== 'Completed' && (
            <button
              onClick={() => handleUpdateStatus('Completed')}
              className="px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all"
            >
              Mark as Completed
            </button>
          )}
          {service.status !== 'Picked Up' && (
            <button
              onClick={() => handleUpdateStatus('Picked Up')}
              className="px-6 py-3 bg-gray-500 text-white rounded-xl font-semibold hover:bg-gray-600 transition-all"
            >
              Mark as Picked Up
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailPage;
